import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  MessageSquare,
  User,
  Shield,
  ChevronDown,
  Dumbbell,
  Heart,
  History,
  Utensils,
  LayoutDashboard,
  Package,
  TrendingUp,
  Timer,
  Trophy,
  Users,
  LogOut,
  Crown,
  Search
} from 'lucide-react';
import { Badge } from './ui/badge';
import { NotificationBell } from './social/NotificationBell';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = '/logo.webp';

export const NavBar = ({ title = '' }) => {
  const { user, api, logout } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await api().get('/messages/unread-count');
        setUnreadCount(res.data.unread_count);
      } catch {
        // silent
      }
    };
    
    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [api]);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/workouts', label: 'Workouts' },
    { path: '/programs', label: 'Programs' },
    { path: '/challenges', label: 'Challenges' },
    { path: '/social', label: 'Social' },
    { path: '/timer', label: 'Timer' },
    { path: '/nutrition', label: 'Nutrition' },
  ];

  return (
    <header className="sticky top-0 z-50 glass border-b border-border">
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard">
            <img
              src={LOGO_URL}
              alt="Ascend & Surpass"
              width="40"
              height="40"
              loading="eager"
              decoding="async"
              className="h-10 w-10"
            />
          </Link>
          {title && (
            <h1 className="font-heading text-2xl text-white uppercase tracking-wider hidden md:block">{title}</h1>
          )}
        </div>
        
        <nav className="flex items-center gap-3 md:gap-6">
          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map(link => (
              <Link 
                key={link.path}
                to={link.path} 
                className={`transition-colors font-medium ${
                  isActive(link.path) ? 'text-primary' : 'text-white/70 hover:text-primary'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Modern glass dock — groups the icon shortcuts into a single
              cohesive pill with subtle blur + inner highlight. A shared
              `motion.div` with `layoutId="dock-indicator"` slides the
              glowing gradient pill smoothly between active icons as the
              user navigates. */}
          <div className="flex items-center gap-1 sm:gap-1.5 p-1 rounded-full
            bg-white/[0.04] border border-white/10 backdrop-blur-md
            shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_4px_14px_rgba(0,0,0,0.35)]">

            {/* Dashboard */}
            <Link
              to="/dashboard"
              className={`group relative flex items-center justify-center h-9 w-9 rounded-full transition-colors duration-200 ${
                isActive('/dashboard') ? 'text-black' : 'text-white/70 hover:text-primary'
              }`}
              data-testid="dashboard-nav-btn"
              aria-label="Dashboard"
            >
              {isActive('/dashboard') && (
                <motion.span
                  layoutId="dock-indicator"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-300 to-primary shadow-[0_0_14px_rgba(212,175,55,0.6)]"
                  aria-hidden="true"
                />
              )}
              <LayoutDashboard className="relative h-[18px] w-[18px] transition-transform group-hover:scale-110" strokeWidth={2.2} />
            </Link>

            {/* Notification Bell - Follow Requests */}
            <NotificationBell />

            {/* Search People */}
            <Link
              to="/discover"
              className={`group relative flex items-center justify-center h-9 w-9 rounded-full transition-colors duration-200 ${
                isActive('/discover') ? 'text-black' : 'text-white/70 hover:text-primary'
              }`}
              data-testid="search-nav-btn"
              aria-label="Discover people"
            >
              {isActive('/discover') && (
                <motion.span
                  layoutId="dock-indicator"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-300 to-primary shadow-[0_0_14px_rgba(212,175,55,0.6)]"
                  aria-hidden="true"
                />
              )}
              <Search className="relative h-[18px] w-[18px] transition-transform group-hover:scale-110" strokeWidth={2.2} />
            </Link>

            {/* Messages Button */}
            <Link
              to="/messages"
              className={`group relative flex items-center justify-center h-9 w-9 rounded-full transition-colors duration-200 ${
                isActive('/messages') ? 'text-black' : 'text-white/70 hover:text-primary'
              }`}
              data-testid="messages-nav-btn"
              aria-label="Messages"
            >
              {isActive('/messages') && (
                <motion.span
                  layoutId="dock-indicator"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-300 to-primary shadow-[0_0_14px_rgba(212,175,55,0.6)]"
                  aria-hidden="true"
                />
              )}
              <MessageSquare className="relative h-[18px] w-[18px] transition-transform group-hover:scale-110" strokeWidth={2.2} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-background shadow-[0_0_8px_rgba(239,68,68,0.6)] z-10">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>

            {/* Profile Button with Dropdown */}
            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className={`group relative flex items-center justify-center h-9 w-9 rounded-full transition-colors duration-200 ${
                  isActive('/profile') ? 'text-black' : 'text-white/70 hover:text-primary'
                }`}
                data-testid="profile-dropdown-toggle"
                aria-label="Profile menu"
              >
                {isActive('/profile') && (
                  <motion.span
                    layoutId="dock-indicator"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-300 to-primary shadow-[0_0_14px_rgba(212,175,55,0.6)]"
                    aria-hidden="true"
                  />
                )}
                <User className="relative h-[18px] w-[18px] transition-transform group-hover:scale-110" strokeWidth={2.2} />
              </button>
            
            <AnimatePresence>
              {profileMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full right-0 mt-2 w-64 bg-card border border-border z-50 overflow-hidden rounded-lg"
                >
                  <Link 
                    to="/subscription" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Crown className="h-5 w-5" />
                    Premium
                  </Link>
                  <Link 
                    to="/profile" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <User className="h-5 w-5" />
                    Profile
                  </Link>
                  <Link 
                    to="/workouts" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Dumbbell className="h-5 w-5" />
                    Workouts
                  </Link>
                  <Link 
                    to="/programs" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Package className="h-5 w-5" />
                    Programs
                  </Link>
                  <Link 
                    to="/progress" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <TrendingUp className="h-5 w-5" />
                    Progress
                  </Link>
                  <Link 
                    to="/challenges" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Trophy className="h-5 w-5" />
                    Challenges
                  </Link>
                  <Link 
                    to="/social" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Users className="h-5 w-5" />
                    Social
                  </Link>
                  <Link 
                    to="/timer" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Timer className="h-5 w-5" />
                    Timer
                  </Link>
                  <Link 
                    to="/favorites" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Heart className="h-5 w-5" />
                    Favorites
                  </Link>
                  <Link 
                    to="/history" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <History className="h-5 w-5" />
                    History
                  </Link>
                  <Link 
                    to="/nutrition" 
                    className="flex items-center gap-3 px-4 py-1 text-base text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Utensils className="h-5 w-5" />
                    Nutrition
                  </Link>
                  <div className="border-t border-border my-0.5" />
                  <button 
                    onClick={() => {
                      setProfileMenuOpen(false);
                      logout();
                    }}
                    className="flex items-center gap-3 px-4 py-1 text-base text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors w-full"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-5 w-5" />
                    Log Out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            </div>

            {/* Admin Button (if admin) — kept inside the glass dock so the
                whole right-side cluster reads as a single modern pill. */}
            {user?.is_admin && (
              <Link
                to="/admin"
                className={`group relative flex items-center justify-center h-9 w-9 rounded-full transition-colors duration-200 ${
                  isActive('/admin') ? 'text-black' : 'text-white/70 hover:text-primary'
                }`}
                data-testid="admin-nav-btn"
                aria-label="Admin"
              >
                {isActive('/admin') && (
                  <motion.span
                    layoutId="dock-indicator"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-300 to-primary shadow-[0_0_14px_rgba(212,175,55,0.6)]"
                    aria-hidden="true"
                  />
                )}
                <Shield className="relative h-[18px] w-[18px] transition-transform group-hover:scale-110" strokeWidth={2.2} />
              </Link>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};
